import { Check, Copy } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import { toast } from "sonner";
import ReactJsonView from "@microlink/react-json-view";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface JsonDocumentViewerProps {
  json: object;
  raw: string;
}

const COPIED_FEEDBACK_MS = 1200;

export function JsonDocumentViewer({ json, raw }: JsonDocumentViewerProps) {
  const [pretty, setPretty] = useState(true);
  const [copied, setCopied] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const resetTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const checkboxId = useId();

  useEffect(() => () => clearTimeout(resetTimer.current), []);

  const displayed = pretty ? JSON.stringify(json, null, 2) : raw;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayed);
    } catch {
      toast.error("Couldn't copy to clipboard.");
      return;
    }

    setCopied(true);
    setTooltipOpen(true);
    clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => {
      setCopied(false);
      setTooltipOpen(false);
    }, COPIED_FEEDBACK_MS);
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Checkbox id={checkboxId} checked={pretty} onCheckedChange={(value) => setPretty(value === true)} />
          <Label htmlFor={checkboxId} className="text-xs font-normal text-muted-foreground">
            Pretty-print JSON
          </Label>
        </div>

        <TooltipProvider>
          <Tooltip open={tooltipOpen} onOpenChange={setTooltipOpen}>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={handleCopy}>
                {copied ? <Check className="h-3.5 w-3.5 text-green-600" /> : <Copy className="h-3.5 w-3.5" />}
                Copy
              </Button>
            </TooltipTrigger>
            <TooltipContent>{copied ? "Copied!" : "Copy JSON to clipboard"}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {pretty ? (
        <div data-testid="json-viewer" className="overflow-x-auto font-mono text-sm">
          <ReactJsonView
            src={json}
            name={false}
            indentWidth={2}
            collapsed={2}
            enableClipboard
            displayDataTypes={false}
            displayObjectSize={false}
            style={{ fontSize: "13px", fontFamily: "inherit", backgroundColor: "transparent" }}
          />
        </div>
      ) : (
        <pre data-testid="json-raw" className="overflow-x-auto whitespace-pre-wrap break-all font-mono text-xs">
          {raw}
        </pre>
      )}
    </div>
  );
}
